import * as moment from "moment";
import { DateTime } from "enums";
import { formatNumberToMoneyWithCurrencySymbol } from "services/money.service";
import { toApiDateTimeFormat } from "services/date.service";
import { filterRequest, dateToTimeAgo, buildItemLookup, mapSplitItems } from "./common.mapper";

// "Time to serve/complete" is computed here, client-side, from the durations the backend
// already returns per status transition (previous_state_duration_seconds) -- no backend
// guessing which literal state name means "served" (confirmed free-form per venue).
const formatDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) {
    return "-";
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (!hours && seconds) parts.push(`${seconds}s`);
  return parts.length ? parts.join(" ") : "0s";
};

const resourceName = (customName, defaultName) => customName || defaultName || "-";

const sumDurationSeconds = (statusHistory = []) =>
  statusHistory.reduce((sum, entry) => sum + (entry.previousStateDurationSeconds || 0), 0);

const mapTimelineEntry = (entry) => ({
  fromState: entry.fromState || "-",
  toState: entry.toState,
  changedAt: entry.changedAt ? moment(entry.changedAt).format(DateTime.I) : "-",
  durationSeconds: entry.previousStateDurationSeconds || 0,
  durationLabel: formatDuration(entry.previousStateDurationSeconds),
  reason: entry.reason,
  deviceId: entry.deviceId,
  actorType: entry.actorType,
});

const mapItem = (item) => {
  const totalDurationSeconds = sumDurationSeconds(item.statusHistory);
  return {
    orderItemId: item.orderItemId,
    itemLocalId: item.itemLocalId,
    productName: item.productName || item.itemLocalId,
    quantity: item.quantity,
    price: formatNumberToMoneyWithCurrencySymbol(item.price || 0),
    taxAmount: formatNumberToMoneyWithCurrencySymbol(item.taxAmount || 0),
    serviceChargeAmount: formatNumberToMoneyWithCurrencySymbol(item.serviceChargeAmount || 0),
    notes: item.notes || "-",
    status: item.status,
    statusHistory: (item.statusHistory || []).map(mapTimelineEntry),
    totalDurationSeconds,
    totalDurationLabel: formatDuration(totalDurationSeconds),
  };
};

const mapOrder = (order) => {
  const totalDurationSeconds = sumDurationSeconds(order.statusHistory);
  return {
    orderId: order.orderId,
    orderLocalId: order.orderLocalId,
    status: order.status,
    orderNote: order.orderNote,
    isPrepaid: Boolean(order.isPrepaid),
    statusHistory: (order.statusHistory || []).map(mapTimelineEntry),
    totalDurationSeconds,
    totalDurationLabel: formatDuration(totalDurationSeconds),
    items: (order.items || []).map(mapItem),
  };
};

const mapBill = (reservationBill, itemLookup) => {
  const bill = reservationBill.bill || {};
  const money = (v) => formatNumberToMoneyWithCurrencySymbol(v || 0);
  return {
    billHeaderId: bill.billHeaderId,
    billLocalId: bill.billLocalId,
    billType: bill.billType,
    status: bill.status,
    grossAmount: money(bill.grossAmount),
    discountAmount: money(bill.discountAmount),
    voucherAmount: money(bill.voucherAmount),
    taxAmount: money(bill.taxAmount),
    netAmount: money(bill.netAmount),
    paidAmount: money(bill.paidAmount),
    openedAt: bill.openedAt ? moment(bill.openedAt).format(DateTime.I) : "-",
    settledAt: bill.settledAt ? moment(bill.settledAt).format(DateTime.I) : "-",
    resolvedTransactionId: reservationBill.resolvedTransactionId,
    splits: (bill.splits || []).map((split) => ({
      billSplitId: split.billSplitId,
      billSplitLocalId: split.billSplitLocalId,
      splitNumber: split.splitNumber,
      splitType: split.splitType,
      status: split.status,
      guestRef: split.guestRef || "-",
      splitAmount: money(split.splitAmount),
      paidAmount: money(split.paidAmount),
      discountAmount: money(split.discountAmount),
      voucherAmount: money(split.voucherAmount),
      reconciliationStatus: split.reconciliationStatus,
      items: mapSplitItems(split.itemsSnapshot, itemLookup),
      payments: (split.payments || []).map((payment) => ({
        transactionId: payment.transactionId,
        method: payment.method,
        amount: money(payment.amount),
        referenceId: payment.referenceId,
        status: payment.status,
        paidAt: payment.paidAt ? moment(payment.paidAt).format(DateTime.I) : "-",
        isSelected: Boolean(payment.isSelected),
      })),
    })),
  };
};

// POST /pos-reservation/search -- one row per reservation.
export const reservationListResponse = {
  posReservationId: { key: "posReservationId" },
  sessionId: { key: "sessionId" },
  status: { key: "status" },
  resourceId: { key: "resourceId" },
  resourceName: {
    transform: ({ src }) => resourceName(src.resourceCustomName, src.resourceDefaultName),
  },
  guestCount: { key: "guestCount" },
  primaryGuestName: { key: "primaryGuestName" },
  openedAt: dateToTimeAgo("openedAt"),
  closedAt: dateToTimeAgo("closedAt"),
  closeReason: { key: "closeReason" },
  orderCount: { key: "orderCount" },
  billCount: { key: "billCount" },
  totalPaidAmount: {
    transform: ({ src }) => formatNumberToMoneyWithCurrencySymbol(src.totalPaidAmount || 0),
  },
};

// GET /pos-reservation/{id}
export const reservationDetailResponse = {
  reservation: {
    transform: ({ src }) => {
      const reservation = src.reservation || {};
      const reservationDurationSeconds =
        reservation.openedAt && reservation.closedAt
          ? moment(reservation.closedAt).diff(moment(reservation.openedAt), "seconds")
          : null;
      return {
        posReservationId: reservation.posReservationId,
        sessionId: reservation.sessionId,
        status: reservation.status,
        openedAt: reservation.openedAt ? moment(reservation.openedAt).format(DateTime.I) : "-",
        closedAt: reservation.closedAt ? moment(reservation.closedAt).format(DateTime.I) : "-",
        closeReason: reservation.closeReason,
        resourceId: reservation.resourceId,
        resourceName: resourceName(src.resourceCustomName, src.resourceDefaultName),
        guestCount: reservation.guestCount,
        reservationDurationLabel: formatDuration(reservationDurationSeconds),
        guests: (reservation.guests || []).map((guest) => ({
          name: guest.name || guest.guestRef || "-",
          guestType: guest.guestType,
          isPrimary: Boolean(guest.isPrimary),
        })),
      };
    },
  },
  orders: {
    transform: ({ src }) => (src.orders || []).map(mapOrder),
  },
  bills: {
    transform: ({ src }) => {
      const itemLookup = buildItemLookup(src.orders);
      return (src.bills || []).map((bill) => mapBill(bill, itemLookup));
    },
  },
  resourceStatusHistory: {
    transform: ({ src }) => (src.resourceStatusHistory || []).map(mapTimelineEntry),
  },
};

// POST /pos-reservation/search request -- dateRange stays optional (no default
// today-only window like transactions) so the list shows all reservations by default.
export const reservationListFilterRequest = {
  ...filterRequest,
  startDateTime: {
    transform: ({ src }) => (src?.dateRange?.[0] ? toApiDateTimeFormat(src.dateRange[0]) : null),
  },
  endDateTime: {
    transform: ({ src }) =>
      src?.dateRange?.[1] ? toApiDateTimeFormat(src.dateRange[1], true) : null,
  },
};
